import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, ShoppingCart, TrendingDown, AlertTriangle, Loader2 } from 'lucide-react';
import { useCostAnalytics, useAnalyticsPeriod } from '@/hooks/useCostAnalytics';

interface CostAnalyticsDashboardProps {
  restaurantId: string;
}

type PeriodPreset = 'today' | 'week' | 'month';

export function CostAnalyticsDashboard({ restaurantId }: CostAnalyticsDashboardProps) {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodPreset>('month');
  const period = useAnalyticsPeriod(selectedPeriod);

  const { data: analytics, isLoading } = useCostAnalytics(restaurantId, period);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const { inventoryValue, purchases, cogs, waste } = analytics;

  // Defensive checks - ensure all values are numbers
  const safeInventoryValue = {
    totalInventoryValue: inventoryValue?.totalInventoryValue ?? 0,
    totalIngredients: inventoryValue?.totalIngredients ?? 0,
    averageValuePerIngredient: inventoryValue?.averageValuePerIngredient ?? 0,
  };

  const safePurchases = {
    totalPurchases: purchases?.totalPurchases ?? 0,
    totalReceipts: purchases?.totalReceipts ?? 0,
    topExpenseIngredient: purchases?.topExpenseIngredient ?? null,
    topExpenseAmount: purchases?.topExpenseAmount ?? 0,
  };

  const safeCogs = {
    totalCogs: cogs?.totalCogs ?? 0,
    totalOrders: cogs?.totalOrders ?? 0,
    averageCogsPerOrder: cogs?.averageCogsPerOrder ?? 0,
  };

  const safeWaste = {
    totalWasteValue: waste?.totalWasteValue ?? 0,
    shortageValue: waste?.shortageValue ?? 0,
    overageValue: waste?.overageValue ?? 0,
    totalValidations: waste?.totalValidations ?? 0,
  };

  return (
    <div className="space-y-4">
      {/* Period Selector - stacked on mobile, row on larger screens */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold shrink-0">{t('inventory.costAnalytics.title')}</h3>
        <div className="flex gap-1.5 sm:gap-2 min-w-0">
          <Button
            variant={selectedPeriod === 'today' ? 'default' : 'outline'}
            size="sm"
            className="flex-1 sm:flex-initial text-xs sm:text-sm min-w-0 px-2 sm:px-3"
            onClick={() => setSelectedPeriod('today')}
          >
            {t('inventory.costAnalytics.today')}
          </Button>
          <Button
            variant={selectedPeriod === 'week' ? 'default' : 'outline'}
            size="sm"
            className="flex-1 sm:flex-initial text-xs sm:text-sm min-w-0 px-2 sm:px-3"
            onClick={() => setSelectedPeriod('week')}
          >
            {t('inventory.costAnalytics.week')}
          </Button>
          <Button
            variant={selectedPeriod === 'month' ? 'default' : 'outline'}
            size="sm"
            className="flex-1 sm:flex-initial text-xs sm:text-sm min-w-0 px-2 sm:px-3"
            onClick={() => setSelectedPeriod('month')}
          >
            {t('inventory.costAnalytics.month')}
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Card 1: Total Inventory Value */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('inventory.costAnalytics.totalInventoryValue')}
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {safeInventoryValue.totalInventoryValue.toFixed(2)} {t('common.currency')}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {safeInventoryValue.totalIngredients} {t('inventory.costAnalytics.ingredient')}
              {safeInventoryValue.totalIngredients > 0 && (
                <span className="block">
                  {t('inventory.costAnalytics.average')}:{' '}
                  {safeInventoryValue.averageValuePerIngredient.toFixed(2)} {t('common.currency')}
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Card 2: Monthly Purchase Costs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('inventory.costAnalytics.purchaseCosts')}
            </CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {safePurchases.totalPurchases.toFixed(2)} {t('common.currency')}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              <p>{safePurchases.totalReceipts} {t('inventory.costAnalytics.receipt')}</p>
              {safePurchases.topExpenseIngredient && (
                <div className="mt-1">
                  <Badge variant="outline" className="text-xs">
                    {safePurchases.topExpenseIngredient}:{' '}
                    {safePurchases.topExpenseAmount.toFixed(2)} {t('common.currency')}
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Cost of Goods Sold (COGS) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('inventory.costAnalytics.costOfGoodsSold')}
            </CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {safeCogs.totalCogs.toFixed(2)} {t('common.currency')}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {safeCogs.totalOrders} {t('inventory.costAnalytics.order')}
              {safeCogs.totalOrders > 0 && (
                <span className="block">
                  {t('inventory.costAnalytics.average')}: {safeCogs.averageCogsPerOrder.toFixed(2)}{' '}
                  {t('common.currency')}
                  {t('inventory.costAnalytics.perOrder')}
                </span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Card 4: Waste/Loss Value */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('inventory.costAnalytics.wasteAndLosses')}
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {safeWaste.totalWasteValue.toFixed(2)} {t('common.currency')}
            </div>
            <div className="flex gap-2 mt-2 text-xs">
              <Badge variant="outline" className="bg-red-50 border-red-200">
                {t('inventory.costAnalytics.shortage')}: {safeWaste.shortageValue.toFixed(2)}
              </Badge>
              <Badge variant="outline" className="bg-green-50 border-green-200">
                {t('inventory.costAnalytics.overage')}: {safeWaste.overageValue.toFixed(2)}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {safeWaste.totalValidations} {t('inventory.costAnalytics.validation')}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
